import { Tab } from "@headlessui/react";
import { ExclamationCircleIcon, PlusIcon, TerminalIcon, TrashIcon } from "@heroicons/react/outline";
import { CurlyBracesIcon, HammerIcon, Link } from "lucide-react";
import { observer } from "mobx-react-lite";
import { createContext, useContext, useMemo, useState } from "react";

import { useParameters } from "../ModelParameters/store/parameters-store";
import { usePlayground } from "../Playground/playground-store";
import useJSONSchema from "../PromptBlueprint/components/hooks/useJSONSchema";
import SchemaEditor from "../SchemaEditor";
import EmptyPlaceholder from "../ui/EmptyPlaceholder";
import { Label } from "../ui/label";

import { ENDPOINTS } from "@/api/application-api";
import { ChooseEntityDialogControlled } from "@/components/ChooseEntityDialog";
import BuiltInToolsDialog from "@/components/FunctionsModal/BuiltInToolsDialog";
import FunctionEditorDialog from "@/components/FunctionsModal/FunctionEditorDialog";
import FunctionOverviewDialog from "@/components/FunctionsModal/FunctionOverviewDialog";
import MCPToolDialog from "@/components/FunctionsModal/MCPToolDialog";
import {
  FunctionDialogContextType,
  FunctionDialogProps,
  ParametersEditorModeEnum,
} from "@/components/FunctionsModal/Types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { Entity, EntityType, getRequiredEntityNumericId } from "@/queries/folder-entities/types";
import { BuiltInTool, Function_, JSONSchema } from "@/types";
import { jsonAuthHeaders } from "@/utils/headers";
import { getTemplateFormat, isBuiltInTool, isMcpTool } from "@/utils/utils";

const FunctionPanelContent = ({
  isAddingFunction,
  editingFunction,
  isShowingBuiltInTools,
  isShowingMcpTool,
}: {
  isAddingFunction: boolean;
  editingFunction: Function_ | null;
  isShowingBuiltInTools: boolean;
  isShowingMcpTool: boolean;
}) => {
  const isEditingMcp = editingFunction && isMcpTool(editingFunction);
  if (isShowingMcpTool || isEditingMcp) {
    return <MCPToolDialog />;
  }

  if (isShowingBuiltInTools || (editingFunction && isBuiltInTool(editingFunction))) {
    return <BuiltInToolsDialog />;
  }

  if (isAddingFunction || editingFunction) {
    return <FunctionEditorDialog />;
  }

  return <FunctionOverviewDialog />;
};

const SchemaControls = ({
  isEditing,
  schema,
  handleSchemaSubmit,
}: {
  isEditing: boolean;
  schema: JSONSchema | boolean;
  handleSchemaSubmit: (schema: JSONSchema, close?: boolean) => void;
}) => {
  if (typeof schema !== "boolean") {
    if (isEditing) {
      return (
        <Button
          variant="destructiveOutline"
          size="sm"
          onClick={() => handleSchemaSubmit(undefined!, false)}
        >
          <TrashIcon className="mr-1 h-4 w-4" /> Remove Schema
        </Button>
      );
    }
    return null;
  }

  return (
    <>
      <EmptyPlaceholder
        message="No schema is defined"
        className="mb-2"
        icon={<CurlyBracesIcon className="h-5 w-5 text-gray-400" />}
      />
      {isEditing && (
        <Button
          variant="default"
          onClick={e => {
            e.preventDefault();
            handleSchemaSubmit(
              {
                type: "object",
                properties: {},
                required: [],
                additionalProperties: false,
              },
              false,
            );
          }}
        >
          <PlusIcon className="mr-2 h-4 w-4" /> Add Schema
        </Button>
      )}
    </>
  );
};

const DEFAULT_CONTEXT: FunctionDialogContextType = {
  functions: [],
  setFunctions: () => undefined,
  functionCall: undefined,
  setFunctionCall: () => undefined,
  functionCallValue: "",
  functionsType: "tools",
  setFunctionsType: () => undefined,
  isAddingFunction: false,
  setIsAddingFunction: () => undefined,
  editingFunction: null,
  setEditingFunction: () => undefined,
  isEditing: false,
  parameterEditorMode: ParametersEditorModeEnum.INTERACTIVE,
  setParameterEditorMode: () => undefined,
  isShowingBuiltInTools: false,
  setIsShowingBuiltInTools: () => undefined,
  isShowingMcpTool: false,
  setIsShowingMcpTool: () => undefined,
  isShowingRegistryTools: false,
  setIsShowingRegistryTools: () => undefined,
  aiAssistantPortalContainer: null,
  onSubmit: () => undefined,
};

const FunctionDialogContext = createContext<FunctionDialogContextType>(DEFAULT_CONTEXT);

export const FunctionDialog = observer((props: FunctionDialogProps) => {
  const [parameterEditorMode, setParameterEditorMode] = useState(
    ParametersEditorModeEnum.INTERACTIVE,
  );
  const [isAddingFunction, setIsAddingFunction] = useState(false);
  const [editingFunction, setEditingFunction] = useState<Function_ | null>(null);
  const [isShowingBuiltInTools, setIsShowingBuiltInTools] = useState(false);
  const [isShowingMcpTool, setIsShowingMcpTool] = useState(false);
  const [isShowingRegistryTools, setIsShowingRegistryTools] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dialogContentElement, setDialogContentElement] = useState<HTMLDivElement | null>(null);

  const functionCallValue =
    typeof props.functionCall === "string" ? props.functionCall : props.functionCall?.name;

  const getToolChoiceValue = (): string | undefined => {
    if (props.toolChoice === undefined) {
      return undefined;
    }

    if (typeof props.toolChoice === "string") {
      return props.toolChoice;
    }

    const name = props.toolChoice?.function?.name;
    if (!name) {
      return undefined;
    }
    const builtIn = props.functions.find(
      f => isBuiltInTool(f as Function_) && (f as unknown as BuiltInTool).name === name,
    );
    return builtIn ? (builtIn as unknown as BuiltInTool).type : name;
  };

  const toolChoiceValue = getToolChoiceValue();

  const isEditing = useMemo(
    () => Boolean(props.setFunctions && props.onSubmit && !props.readonly),
    [props.setFunctions, props.onSubmit, props.readonly],
  );

  const contextValue = useMemo(
    () => ({
      ...DEFAULT_CONTEXT,
      functions: props.functions,
      setFunctions: props.setFunctions || DEFAULT_CONTEXT.setFunctions,
      functionCall: props.functionCall,
      setFunctionCall: props.setFunctionCall || DEFAULT_CONTEXT.setFunctionCall,
      functionCallValue,
      functionsType: props.functionsType,
      setFunctionsType: props.setFunctionsType || DEFAULT_CONTEXT.setFunctionsType,
      isAddingFunction,
      toolChoiceValue,
      setIsAddingFunction,
      editingFunction,
      setEditingFunction,
      isEditing,
      parameterEditorMode,
      setParameterEditorMode,
      isShowingBuiltInTools,
      setIsShowingBuiltInTools,
      isShowingMcpTool,
      setIsShowingMcpTool,
      isShowingRegistryTools,
      setIsShowingRegistryTools,
      aiAssistantPortalContainer: dialogContentElement,
      onSubmit: props.onSubmit || DEFAULT_CONTEXT.onSubmit,
    }),
    [
      props.functions,
      props.setFunctions,
      props.functionCall,
      props.setFunctionCall,
      props.functionsType,
      props.setFunctionsType,
      props.onSubmit,
      functionCallValue,
      isAddingFunction,
      editingFunction,
      isEditing,
      parameterEditorMode,
      isShowingBuiltInTools,
      isShowingMcpTool,
      isShowingRegistryTools,
      toolChoiceValue,
      dialogContentElement,
    ],
  );

  const playground = usePlayground();
  const parametersStore = useParameters();
  const auth = useAuth();
  const userContext = useUser();
  const userToken = auth?.userToken ?? "";
  const activeWorkspaceId = userContext?.activeWorkspaceId;

  // Fallback to false so schema is never undefined (SchemaControls expects boolean | JSONSchema)
  const schema = useJSONSchema() || props.jsonSchema || false;

  const template = playground.blueprint?.prompt_template;
  const templateFormat = getTemplateFormat(template);
  const isMenuItemTrigger = props.triggerVariant === "menu-item";

  const handleSchemaSubmit = (newSchema: JSONSchema, close = true) => {
    if (newSchema) {
      playground.updateJSONSchema(newSchema);
      parametersStore.setJSONSchema(newSchema);
    } else {
      playground.updateJSONSchema(null);
      parametersStore.setJSONSchema(null);
    }
    props.onJSONChange?.(newSchema);
    if (close) {
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <FunctionDialogContext.Provider value={contextValue}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`relative h-8 items-center gap-1.5 text-gray-700 transition-colors hover:bg-gray-100 ${
                isMenuItemTrigger
                  ? "!flex !w-full !justify-start !rounded-md !px-2 !text-left !font-normal"
                  : "inline-flex rounded-full px-2.5"
              } ${
                // schema is just true if schema option is available
                (contextValue.functions?.length > 0 ||
                  (typeof schema === "object" && Object.keys(schema).length > 0)) &&
                "after:absolute after:right-1.5 after:top-1.5 after:h-1.5 after:w-1.5 after:rounded-full after:bg-blue-500"
              }`}
            >
              <TerminalIcon className="h-[17px] w-[17px] shrink-0 text-gray-900" />
              <span className="text-sm leading-5">Tools & Output</span>
            </Button>
          </DialogTrigger>
          <DialogContent
            ref={setDialogContentElement}
            className="flex max-h-[800px] min-h-[600px] max-w-6xl flex-col"
          >
            <DialogHeader className="text-center text-xl">
              <div className="flex flex-col">
                <Label className="text-xl font-bold">Tool & Output Editor</Label>
                <p className="text-sm text-gray-500">
                  <a
                    href={
                      selectedTabIndex === 0
                        ? "https://platform.openai.com/docs/guides/gpt/function-calling"
                        : "https://json-schema.org/understanding-json-schema/"
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-gray-500 hover:text-gray-400"
                  >
                    Learn more on the {selectedTabIndex === 0 ? "OpenAI" : "JSON Schema"} docs
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link className="inline h-4 w-4 pl-1" />
                  </a>
                </p>
              </div>
            </DialogHeader>
            <Tab.Group selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
              <Tab.List className="sticky top-0 z-30  flex rounded-xl bg-slate-50 p-1.5 shadow-sm ring-1 ring-slate-200/60">
                <Tab
                  className={({ selected }) =>
                    `group relative w-full rounded-lg px-4 py-3 text-sm font-medium leading-5 transition-all duration-200 ease-in-out
                  ${
                    selected
                      ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                      : "text-slate-600 hover:bg-white/50 hover:text-slate-900"
                  }`
                  }
                >
                  <div className="flex items-center justify-center space-x-2">
                    <HammerIcon className="h-4 w-4" />
                    <span>Tools</span>
                  </div>
                  <span className="absolute left-1/2 top-full z-50 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-4 py-2.5 text-xs text-slate-50 shadow-xl ring-1 ring-slate-800/5 group-hover:block">
                    Define tools that can be called
                  </span>
                </Tab>
                <Tab
                  className={({ selected }) =>
                    `group relative w-full rounded-lg px-4 py-3 text-sm font-medium leading-5 transition-all duration-200 ease-in-out
                  ${!schema ? "cursor-not-allowed opacity-50" : ""}
                  ${
                    selected
                      ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                      : "text-slate-600 hover:bg-white/50 hover:text-slate-900"
                  }`
                  }
                  disabled={!schema}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <CurlyBracesIcon className="h-4 w-4" />
                    <span>Structured Output</span>
                    {!schema && <ExclamationCircleIcon className="h-4 w-4 text-amber-500" />}
                  </div>
                  <span className="absolute left-1/2 top-full z-50 mt-2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-4 py-2.5 text-xs text-slate-50 shadow-xl ring-1 ring-slate-800/5 group-hover:block">
                    {!schema
                      ? "To use Structured Outputs, select a compatible model such as gpt-4o"
                      : "Define JSON schema for structured model outputs"}
                  </span>
                </Tab>
              </Tab.List>
              <Tab.Panels className="flex-1 overflow-auto px-4">
                <Tab.Panel className="h-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2">
                  <FunctionPanelContent
                    isAddingFunction={isAddingFunction}
                    editingFunction={editingFunction}
                    isShowingBuiltInTools={isShowingBuiltInTools}
                    isShowingMcpTool={isShowingMcpTool}
                  />
                </Tab.Panel>
                <Tab.Panel
                  className={`${
                    !isEditing && " select-none "
                  } h-full rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2`}
                >
                  <div className="mb-2">
                    <SchemaControls
                      isEditing={isEditing}
                      schema={schema}
                      handleSchemaSubmit={handleSchemaSubmit}
                    />
                  </div>
                  {typeof schema !== "boolean" && (
                    <SchemaEditor
                      readonly={!isEditing}
                      schema={schema}
                      format={templateFormat}
                      onSubmit={handleSchemaSubmit}
                      aiAssistantPortalContainer={dialogContentElement}
                    />
                  )}
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </DialogContent>
        </FunctionDialogContext.Provider>
      </Dialog>
      <ChooseEntityDialogControlled
        type={EntityType.TOOL}
        open={isShowingRegistryTools}
        onOpenChange={setIsShowingRegistryTools}
        config={{ selectLatest: false }}
        disabled={(entity: Entity) =>
          props.functions.some(
            fn =>
              typeof fn === "object" &&
              fn !== null &&
              "type" in fn &&
              (fn as unknown as { type: string }).type === "registry" &&
              (fn as unknown as { tool_registry_id: number }).tool_registry_id === entity.id,
          )
        }
        onSelect={async (entity: Entity) => {
          const toolRegistryId = getRequiredEntityNumericId(entity.id);
          let hasExecution = false;
          if (entity.label) {
            hasExecution = entity.labels_with_execution?.includes(entity.label) ?? false;
          } else if (entity.version_number !== undefined && entity.version_number !== null) {
            hasExecution = entity.versions_with_execution?.includes(entity.version_number) ?? false;
          } else {
            hasExecution = entity.has_execution ?? false;
          }
          const registryTool = {
            type: "registry" as const,
            tool_registry_id: toolRegistryId,
            label: entity.label ?? null,
            version_number: entity.version_number ?? null,
            _resolved_name: entity.function_name ?? entity.name,
            _has_execution: hasExecution,
          };
          if (props.setFunctions) {
            props.setFunctions([...props.functions, registryTool as unknown as Function_]);
          }
          setIsShowingRegistryTools(false);

          if (activeWorkspaceId) {
            let url = ENDPOINTS.tool_registry.get(activeWorkspaceId, toolRegistryId);
            const params = new URLSearchParams();
            if (entity.label) {
              params.set("label", entity.label);
            } else if (entity.version_number) {
              params.set("version", String(entity.version_number));
            }
            if (params.toString()) {
              url += `?${params}`;
            }

            const res = await fetch(url, { headers: jsonAuthHeaders(userToken) });
            const data = await res.json();
            const toolDef = data?.resolved_version?.tool_definition;
            if (toolDef) {
              playground.registerToolDefinition(toolRegistryId, toolDef);
            }
          }
        }}
        currentFolderId={null}
      />
    </>
  );
});

export const useFunctionDialogContext = () => {
  const context = useContext(FunctionDialogContext);
  if (!context) {
    throw new Error("useFunctionDialogContext must be used within a FunctionDialogProvider");
  }
  return context;
};
